import { Entities_Modules_Module } from '@suntex.fim/api-client';
import { ModuleTagging } from '@suntex.fim/framework';
import {
  NodeModel,
  StrictNodeModel,
  resolveConstruction,
  resolveFilterPath,
} from '@suntex.fim/hub';

function labelToString(label?: NodeModel['label']): string | undefined {
  if (typeof label === 'string') return label;
  return label?.aria;
}

export function getLabelText(node: NodeModel): string | undefined {
  return labelToString(node.label) ?? labelToString(node.filtered?.label);
}

export function getEffectiveChildren(
  node: StrictNodeModel,
  moduleTagging: readonly ModuleTagging[],
): readonly StrictNodeModel[] {
  const children =
    node.filterTagPath && node.filterRoot
      ? resolveFilterPath(node, moduleTagging)
      : node.children;
  return children.filter((c) => !c.hidden);
}

/**
 * Returns true when the node's subtree is entirely under construction, i.e. it
 * has at least one module-leaf descendant and every module-leaf descendant is
 * in `underConstructionSet`. Mirrors the canvas hub rule so the catalog agrees
 * with the radial view on which containers are offline. A leaf node is under
 * construction simply when its own slug is in the set.
 */
export function isNodeUnderConstruction(
  node: StrictNodeModel,
  moduleTagging: readonly ModuleTagging[],
  underConstructionSet: Set<string>,
): boolean {
  return resolveConstruction(
    node,
    (n) => n.moduleSlug,
    (n) => getEffectiveChildren(n, moduleTagging),
    (moduleSlug) => underConstructionSet.has(moduleSlug),
  ).underConstruction;
}

export function collectModules(
  node: StrictNodeModel,
  moduleMap: Map<string, Entities_Modules_Module>,
  moduleTagging: readonly ModuleTagging[],
): Entities_Modules_Module[] {
  const found: Entities_Modules_Module[] = [];
  if (node.moduleSlug) {
    const m = moduleMap.get(node.moduleSlug);
    if (m) found.push(m);
  }
  for (const child of getEffectiveChildren(node, moduleTagging)) {
    found.push(...collectModules(child, moduleMap, moduleTagging));
  }
  return found;
}
